<template>
	<!-- <CookieConsent /> -->
	<ReadingProgressBar
		v-if="isMounted && showProgressBar"
		:class="{
			'has-cookie-consent-banner': doestNotHaveCookie
		}"
	/>

	<mobile-menu
		v-if="showMenu"
		:navigation="navigation"
		:toggle="() => (showMenu = false)"
	/>

	<!-- Full-screen search overlay (phone) -->
	<HeaderSearch
		v-if="showMobileSearch"
		fullscreen
		@close="showMobileSearch = false"
	/>

	<header
		ref="header"
		class="header"
		:class="{
			'has-cookie-consent-banner': doestNotHaveCookie
		}"
	>
		<div class="mobile-header-content">
			<div class="flex items-center gap-3 min-w-0">
				<NuxtLink
					to="/"
					class="shrink-0"
				>
					<img
						src="/images/logo.svg"
						width="186"
						height="26"
						alt="timothy sykes logo"
						class="mobile-logo"
					/>
				</NuxtLink>

				<!-- Tablet inline search (md to <xl) -->
				<div class="hidden md:block">
					<HeaderSearch
						@open="showSearchBar = true"
						@close="showSearchBar = false"
					/>
				</div>
			</div>

			<div class="flex items-center gap-2 ml-auto">
				<!-- Phone: search icon button opens full-screen search -->
				<button
					class="search-icon-btn md:hidden"
					aria-label="Open search"
					@click="showMobileSearch = true"
				>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
							stroke="white"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>

				<!-- Phone: Watch Live blinking dot -->
				<a
					ref="mobileWatchLiveLink"
					:href="watchLiveHref"
					target="_blank"
					rel="noopener noreferrer"
					class="watch-live-dot md:hidden"
					aria-label="Watch Live"
					@click.passive="onWatchLiveClick"
				>
					<span class="blob red"></span>
				</a>

				<!-- Tablet: Watch Live button -->
				<div class="header-watch-live hidden md:block">
					<a
						ref="tabletWatchLiveLink"
						:href="watchLiveHref"
						target="_blank"
						rel="noopener noreferrer"
						class="nav-link live flex items-center justify-center watch-live-link"
					>
						<span class="blob red"></span>
						WATCH LIVE
					</a>
				</div>

				<button
					class="mobile-menu-btn"
					aria-label="Mobile menu button"
					@click="showMenu = true"
				>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M3 12H15M3 6H21M3 18H21"
							stroke="white"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>
		</div>
		<div class="header-content container xl:container mx-auto">
			<NuxtLink to="/">
				<img
					src="/images/logo.svg"
					width="186"
					height="26"
					alt="timothy sykes logo"
				/>
			</NuxtLink>

			<!-- Inline expandable search bar (desktop) -->
			<HeaderSearch
				class="hidden xl:block"
				@open="showSearchBar = true"
				@close="showSearchBar = false"
			/>

			<nav
				class="nav-link-container hidden xl:flex items-center transition-opacity duration-200"
				:class="{ 'opacity-0 pointer-events-none': showSearchBar }"
				role="navigation"
				aria-label="Main"
			>
				<div
					v-for="menu in navigation.filter(
						(m) => m.post_title?.toLowerCase() !== 'blog'
					)"
					:key="menu.ID"
					class="nav-link-block"
					tabindex="0"
				>
					<NuxtLink
						v-if="menu.url && menu.url !== '#'"
						:to="menu.url"
						class="nav-link"
					>
						<span class="uppercase">
							{{ menu.post_title }}
						</span>
					</NuxtLink>
					<button
						v-else
						type="button"
						class="nav-link"
					>
						<span class="uppercase">
							{{ menu.post_title }}
						</span>
					</button>

					<div
						v-if="menu.children && menu.children.length && showSubmenu"
						class="nav-submenu-container"
					>
						<div class="nav-container xl:container mx-auto grid grid-cols-4">
							<span
								ref="caret"
								class="caret"
							>
								<svg
									width="55"
									height="26"
									viewBox="0 0 55 26"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M25.9047 1.03597L2.8143 16.877C-1.23678 19.6562 0.73011 26 5.64287 26H49.7245C54.5221 26 56.5659 19.8988 52.7364 17.0089L31.7451 1.1679C30.0291 -0.127078 27.6774 -0.180202 25.9047 1.03597Z"
										fill="#EFF6FF"
									/>
								</svg>
							</span>
							<div
								v-for="(submenu, i) in menu.children"
								:key="i"
								class="nav-menu-block"
							>
								<Heading
									tag="h4"
									:text="submenu.post_title"
									class="nav-submenu-title"
								/>

								<NuxtLink
									v-for="item in submenu.children"
									:key="item.url"
									class="nav-submenu-link"
									:to="item.url"
									:target="checkPath(item.url)"
									@click="closeMenu"
								>
									{{ item.title }}
								</NuxtLink>
							</div>
						</div>
					</div>
				</div>

				<button
					ref="signInButton"
					class="h-[32px] w-[32px] flex items-center justify-center ml-[12px] mr-[12px]"
					name="sign-in-button"
					aria-labelledby="sign-in-button"
					@click="showSignInMenu"
				>
					<svg
						stroke="white"
						fill="none"
						stroke-width="0"
						viewBox="0 0 24 24"
						height="24"
						width="24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M16 9C16 11.2091 14.2091 13 12 13C9.79086 13 8 11.2091 8 9C8 6.79086 9.79086 5 12 5C14.2091 5 16 6.79086 16 9ZM14 9C14 10.1046 13.1046 11 12 11C10.8954 11 10 10.1046 10 9C10 7.89543 10.8954 7 12 7C13.1046 7 14 7.89543 14 9Z"
							fill="white"
						></path>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1ZM3 12C3 14.0902 3.71255 16.014 4.90798 17.5417C6.55245 15.3889 9.14627 14 12.0645 14C14.9448 14 17.5092 15.3531 19.1565 17.4583C20.313 15.9443 21 14.0524 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12ZM12 21C9.84977 21 7.87565 20.2459 6.32767 18.9878C7.59352 17.1812 9.69106 16 12.0645 16C14.4084 16 16.4833 17.1521 17.7538 18.9209C16.1939 20.2191 14.1881 21 12 21Z"
							fill="white"
						></path>
					</svg>
					<div
						v-if="showSignIn"
						class="absolute top-[32px] pt-[32px]"
						@mouseleave="showSignInMenu"
					>
						<div
							class="rounded-[12px] border-slate-200 border-solid border-[1px] pt-[8px] px-[16px] w-[155px] h-55 pb-[8px] bg-white flex flex-col"
						>
							<NuxtLink
								class="text-slate-700 py-[8px] nav-submenu-link"
								href="https://alerts.timothysykes.com"
								target="_blank"
							>
								Student Portal
							</NuxtLink>
						</div>
					</div>
				</button>

				<span class="flex gap-3">
					<a
						ref="desktopWatchLiveLink"
						:href="watchLiveHref"
						target="_blank"
						rel="noopener noreferrer"
						class="nav-link live flex items-center justify-center watch-live-link"
					>
						<span class="blob red"></span>
						WATCH LIVE
					</a>
				</span>
			</nav>
		</div>
	</header>

	<ClientOnly>
		<template v-if="router.path === '/' && store.isLoading">
			<Loading />
		</template>
	</ClientOnly>
</template>

<script setup>
const isMounted = ref(false);

const showMenu = ref(false);
const showSubmenu = ref(true);
const showSignIn = ref(false);
const showSearchBar = ref(false);
const showMobileSearch = ref(false);
const header = ref();
const caret = ref();
const router = useRoute();
const showProgressBar = ref(false);
const { env } = useRuntimeConfig().public;
const doestNotHaveCookie = ref(false);
const mobileWatchLiveLink = ref(null);
const tabletWatchLiveLink = ref(null);
const desktopWatchLiveLink = ref(null);

const AIZEN_BASE_URL = "https://aizen-ad-manager.up.railway.app";
const WATCH_LIVE_SLOT_ID = "f3dfec03-2a1d-4b79-b0fc-600ec848489f";
const WATCH_LIVE_FALLBACK_URL =
	"http://servedbyadbutler.com/redirect.spark?MID=181458&plid=2476009&setID=750284&channelID=0&CID=901521&banID=522028156&PID=0&textadID=0&tc=1&rnd=5507714&scheduleID=2394595&placementScheduleId=2394595&adItemScheduleId=0&adSize=0x0&metadata=%5B%5D&mt=1780682090767950&sw=1728&sh=1117&spr=2&referrer=http%3A%2F%2Flocalhost%3A3000%2F&request_uuid=89032023-77d3-47e3-9297-04c488eb7797&hc=d17bb0c34729d0073081fe9d3f7c8ba99e59c8d1&location=";

const watchLiveAd = useState("watch-live-aizen-ad", () => null);
const watchLiveAdLoading = useState("watch-live-aizen-loading", () => false);
const watchLiveImpressionTracked = useState(
	"watch-live-aizen-impression-tracked",
	() => false
);

const normalizeAizenUrl = (url) => {
	if (!url) {
		return null;
	}
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	return `${AIZEN_BASE_URL}${url}`;
};

const { pageKey } = usePageKey()
const watchLiveCtx = computed(() => ({ pageKey: pageKey.value, surface: "nav" }))
const watchLiveHref = computed(() =>
	buildAdClickUrl(
		normalizeAizenUrl(watchLiveAd.value?.clickUrl) ||
			watchLiveAd.value?.destinationUrl ||
			WATCH_LIVE_FALLBACK_URL,
		watchLiveCtx.value
	)
);
const onWatchLiveClick = () => fireAdClickEvent(watchLiveCtx.value)

// Only one of the three breakpoint variants is on screen at a time, so each
// gets its own watcher and they share a single fired flag.
const watchLiveImpressionUrl = () =>
	normalizeAizenUrl(watchLiveAd.value?.impressionUrl);

[mobileWatchLiveLink, tabletWatchLiveLink, desktopWatchLiveLink].forEach((el) => {
	useAizenImpression(el, watchLiveImpressionUrl, {
		firedRef: watchLiveImpressionTracked
	});
});

const fetchWatchLiveAd = async () => {
	if (watchLiveAd.value || watchLiveAdLoading.value) {
		return;
	}

	watchLiveAdLoading.value = true;
	try {
		const adResponse = await $fetch(
			`${AIZEN_BASE_URL}/api/slots/${WATCH_LIVE_SLOT_ID}/serve`
		);

		if (adResponse?.clickUrl || adResponse?.destinationUrl) {
			watchLiveAd.value = adResponse;
		}
	} catch {
	} finally {
		watchLiveAdLoading.value = false;
	}
};

const scheduleWatchLiveAdFetch = () => {
	const load = () => {
		fetchWatchLiveAd();
	};

	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(load, { timeout: 2000 });
		return;
	}

	window.setTimeout(load, 0);
};

// Watch for route changes and update showProgressBar
const config = useRuntimeConfig();

// Use useState to ensure consistent state between SSR and client hydration
const navigation = useState("navigation-menu-state", () => []);

// Fetch navigation data only if not already loaded
if (navigation.value.length === 0) {
	try {
		const response = await $fetch(
			`${config.public.hostname}wp-json/custom/v1/menu/28771`
		);
		if (response && Array.isArray(response)) {
			navigation.value = response;
		}
	} catch (error) {
		// Ignore navigation fetch errors and keep previous state.
	}
}

const toggleHeaderClasses = (header, $route) => {
	if (!header) {
		return;
	}
	if (!["index", "widgetpage", "blog-slug"].includes($route.name)) {
		return;
	}

	const scrollY = window.scrollY;
	const isHome = $route.name === "index";

	if (scrollY > 10) {
		if (header) {
			header.classList.toggle(isHome ? "home" : "has-progress-bar", true);
			if (!isHome && header) {
				header.style.marginTop = 0;
			}
		}
	} else if (header) {
		header.classList.toggle(isHome ? "home" : "has-progress-bar", false);
		if (!isHome && header) {
			header.style.marginTop = "-10px";
		}
	}
};

onMounted(async () => {
	isMounted.value = true;
	if (process.client) {
		if (router.name === "index") {
			header.value.classList.add("home");
		}

		if (["blog-slug", "widgetpage"].includes(router.name)) {
			showProgressBar.value = true;
			if (header.value) {
				header.value.style.marginTop = "-10px";
			}
		}

		if (["blog-slug", "index", "widgetpage"].includes(router.name)) {
			window.addEventListener(
				"scroll",
				() => toggleHeaderClasses(header.value, router),
				{
					passive: true
				}
			);
		}

		const cookieThing = !checkCookieConsent();

		doestNotHaveCookie.value = cookieThing;

		if (!checkCookieConsent()) {
			await setTimeout(() => {
				doestNotHaveCookie.value = false;
			}, 12000);
		}

		scheduleWatchLiveAdFetch();
	}
});

onBeforeUnmount(() => {
	if (process.client) {
		window.removeEventListener("scroll", () =>
			toggleHeaderClasses(header.value, router)
		);
	}
});

watch(
	() => router.path,
	(newPath, oldPath) => {
		if (process.client && newPath !== oldPath) {
			showMobileSearch.value = false;
			window.scrollTo(0, 0);
		}
	}
);

watch(
	() => router.name,
	(newPath, oldPath) => {
		if (process.client) {
			if (newPath === "blog-slug" || newPath === "widgetpage") {
				if (header && header.value) {
					header.value.style.marginTop = "-10px";
				}
				showProgressBar.value = true;
			} else {
				showProgressBar.value = false;
			}

			if (newPath !== oldPath) {
				if (
					header.value &&
					header.value.classList.contains("home") &&
					newPath !== "index"
				) {
					header.value.classList.remove("home");
				}
				if (
					header.value &&
					header.value.classList.contains("has-progress-bar") &&
					newPath !== "blog-slug"
				) {
					header.value.classList.remove("has-progress-bar");
				}
				window.removeEventListener("scroll", () =>
					toggleHeaderClasses(header.value, router)
				);
			}

			if (newPath === "plans" || oldPath === "plans") {
				setTimeout(() => {
					showMenu.value = false;
				}, 1000);
			}

			if (
				newPath === "index" ||
				newPath === "blog-slug" ||
				newPath === "widgetpage"
			) {
				return window.addEventListener(
					"scroll",
					() => toggleHeaderClasses(header.value, router),
					{ passive: true }
				);
			}
		}
	}
);

watch(header, (newNav) => {
	try {
		if (process.client && newNav) {
			const links = document.querySelectorAll(`.nav-submenu-link`);

			[...links].map((link) => {
				link.addEventListener("click", (e) => {
					const linkToBlur = document.activeElement;
					const opensNewWindow = link.getAttribute("target") === "_blank";

					if (
						linkToBlur.parentElement.parentElement &&
						linkToBlur.parentElement.parentElement.parentElement
					) {
						linkToBlur.parentElement.parentElement.parentElement.style.pointerEvents =
							"none";

						setTimeout(() => {
							linkToBlur.parentElement.parentElement.parentElement.style.pointerEvents =
								"auto";
						}, 50);
					}
					if (!opensNewWindow) {
						e.preventDefault();
					}
				});

				return null;
			});
		}
	} catch (error) {
		if (env === "dev") {
			// eslint-disable-next-line no-console
			console.log("ERROR IN NAVIGATION", error);
		}
	}
});

const showSignInMenu = () => {
	showSignIn.value = !showSignIn.value;
};

const closeMenu = () => {
	if (router.path === "/") {
		store.manualToggleLoading(true);
	}

	showSubmenu.value = !showSubmenu.value;
	if (showSubmenu.value !== true) {
		setTimeout(() => closeMenu(), 200);
	}
};

const checkPath = (url) => {
	const arr = [
		"https://www.instagram.com/timothysykes/",
		"https://www.facebook.com/timsykesfans/",
		"https://profit.ly/user/timothysykes/chart?aff=11983",
		"https://karmagawa.org/",
		"https://twitter.com/timothysykes",
		"https://www.youtube.com/channel/UCoSG43KFjTe0trsteSQ46Ng",
		"https://t.me/timsykeslive"
	];

	if (arr.includes(url)) {
		return "_blank";
	}
};
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.mobile-menu-btn {
	background-color: transparent;
	border-radius: 8px;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;

	&.close {
		background-color: transparent;
	}
}

.search-icon-btn {
	background-color: transparent;
	border-radius: 8px;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.watch-live-dot {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px;

	.blob {
		margin: 0;
		left: 0;
	}
}

.header.home.active {
	background-color: $primary-900;
}
.header {
	background-color: $primary-900;
	position: sticky;
	transition: all 100ms ease-in;
	z-index: 999;
	top: 0;

	&.home {
		background-color: $primary-900;
	}

	padding: 0 16px;

	&.has-progress-bar {
		margin-top: 0;
		top: 10px;
	}
}

.nav-link-block:focus .nav-link::before,
.nav-link-block:hover .nav-link::before {
	background-color: transparent;
	width: 100%;
	height: 32px;
	display: block;
	content: "";
	position: absolute;
	bottom: -32px;
}

.nav-link-block:focus .nav-link,
.nav-link-block:hover .nav-link {
	color: $secondary-500;
}

.nav-link-block:focus .nav-submenu-container,
.nav-link-block:hover .nav-submenu-container {
	display: block;
}

.nav-link-block:focus {
	outline: 1px solid $primary-300;
}

.nav-submenu-container {
	display: none;

	.container {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		grid-auto-flow: dense;
	}
}

.nav-menu-block {
	display: flex;
	flex-direction: column;
	column-gap: 16px;
}

.nav-submenu-container {
	position: absolute;
	width: 100%;
	left: 0;
	top: 59px;
	padding: 4px 0;
	z-index: 9999;
	max-width: 1200px;
	right: 0;
	margin: auto;
}

.nav-container {
	background-color: $primary-50;
	border-radius: 8px;
	filter: drop-shadow(0px 2px 9px rgba(0, 0, 0, 0.12));
	padding: 32px;
}

.nav-submenu-title {
	color: $neutral-800;
	font-size: 20px;
	font-family: Rubik;
	margin-bottom: 12px;
	font-weight: 700;
}

.nav-submenu-link {
	font-size: 16px;
	font-weight: 400;
	padding: 8px 16px;
	line-height: 1.25;
	color: $neutral-800;
	display: inline-block;
	font-family: Rubik;
	border-left: 4px solid $primary-700;

	&:hover {
		text-decoration: underline;
		text-decoration-color: $secondary-500;
		font-weight: 600;
	}
}

span.caret {
	display: none;
	position: absolute;
	left: 0;
	right: 0;
	width: 64px;
	margin: auto;

	top: -16px;

	svg {
		width: 64px;
	}
}

span.caret {
	@include breakpoint(lg) {
		display: block;
	}
}

.mobile-logo {
	height: 28px;
	width: auto;
}
.header-content {
	display: none;
	width: 100%;
	padding: 10px 0;
	@include breakpoint(xl) {
		padding: 12px 0;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
}
.mobile-header-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	@include breakpoint(xl) {
		display: none;
	}

	.container {
		@include breakpoint(xl) {
			max-width: 1200px;
		}
		margin: auto;
		padding: 0;
	}
	padding: 10px 0;
}
</style>
